import { useCallback, useEffect, useState } from "react";
import { Spinner, Table } from "react-bootstrap";
import { api } from "../../helpers/apiCore";
import { endpoints } from "../../helpers/Endpoints";
import { ACTIVITY_COLUMNS } from "../../lib/config";
import { PaginationData } from "../../types";
import PaginationComponent from "../../components/PaginationComponent";
import FormInput from "../../components/FormInput";
import searchIcon from "../../assets/icons/search.svg";
import moment from "moment";
import "../../styles/activity-log.css";

interface User {
  first_name: string;
  last_name: string;
  email: string;
}

interface Activity {
  id: number;
  user: User | null;
  action: string;
  created_at: string;
}

const ActivityLog = () => {
  const [activities, setActivities] = useState<Activity[]>([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [paginationData, setPaginationData] = useState<PaginationData>({
    currentPage: 1,
    totalPages: 1,
    total: 0,
    perPage: 10,
  });

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 500);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  const fetchActivities = useCallback(async () => {
    setIsLoading(true);
    try {
      api.setAuthorization(api.getLoggedInUser());
      const params = new URLSearchParams({
        page: paginationData.currentPage.toString(),
        per_page: paginationData.perPage.toString(),
        sortBy: "created_at",
        sortOrder: "desc",
      });
      if (debouncedSearchTerm.trim()) {
        // Backend examples use `search`, but some endpoints in this app use `searchQuery`.
        // Setting both keeps us compatible across environments.
        params.set("search", debouncedSearchTerm.trim());
        params.set("searchQuery", debouncedSearchTerm.trim());
      }
      if (startDate) {
        params.set("startDate", moment(startDate).format("YYYY-MM-DD"));
      }
      if (endDate) {
        params.set("endDate", moment(endDate).format("YYYY-MM-DD"));
      }

      const response = await api.get(endpoints.getActivities, params);
      const { data, pageInfo } = response.data;

      setActivities(data || []);
      setPaginationData({
        currentPage: pageInfo?.currentPage || 1,
        totalPages: pageInfo?.totalPages || 1,
        total: pageInfo?.total || 0,
        perPage: pageInfo?.perPage || 10,
      });
    } catch (error) {
      console.error("Error fetching activities:", error);
      setActivities([]);
    } finally {
      setIsLoading(false);
    }
  }, [debouncedSearchTerm, paginationData.currentPage, paginationData.perPage, startDate, endDate]);

  useEffect(() => {
    fetchActivities();
  }, [fetchActivities]);

  const handleSearchChange = (value: string) => {
    setSearchTerm(value);
    setPaginationData(prev => ({ ...prev, currentPage: 1 }));
  };

  const handlePaginate = (pageNumber: number) => {
    setPaginationData(prev => ({ ...prev, currentPage: pageNumber }));
  };

  const getSerialNumber = (index: number) =>
    (paginationData.currentPage - 1) * paginationData.perPage + index + 1;

  const renderTableCell = (columnKey: string, activity: Activity) => {
    switch (columnKey) {
      case "Name":
        return `${activity?.user?.first_name || ""} ${activity?.user?.last_name || ""}`.trim();
      case "Email":
        return activity?.user?.email || "-";
      case "Action":
        return activity?.action || "-";
      case "Date":
        return activity?.created_at ? moment(activity.created_at).format("DD-MM-YYYY / hh:mmA") : "-";
      default:
        return "-";
    }
  };

  return (
    <>
      <div className="component-body">
        <div className="page-title">
          <h2>Activity Log</h2>
        </div>

        <div className="table-topbar">
          <div className="search_bar">
            <img src={searchIcon} alt="search" />
            <FormInput
              name="search"
              placeholder="Search activities..."
              className="search-bar-input"
              value={searchTerm}
              onChange={e => handleSearchChange(e.target.value)}
            />
          </div>

          <div className="range-input-section mb-3 mt-2">
            <FormInput
              label="From:"
              type="date"
              name="startDate"
              value={startDate}
              onChange={e => {
                setStartDate((e.target as HTMLInputElement).value);
                setPaginationData(prev => ({ ...prev, currentPage: 1 }));
              }}
            />
            <FormInput
              label="To:"
              type="date"
              name="endDate"
              value={endDate}
              onChange={e => {
                setEndDate((e.target as HTMLInputElement).value);
                setPaginationData(prev => ({ ...prev, currentPage: 1 }));
              }}
            />
          </div>
        </div>

        <div className="table-body">
          <Table>
            <thead>
              <tr>
                {/* <th className="custom-thead-bg">S/N</th> */}
                {ACTIVITY_COLUMNS.map(col => (
                  <th key={col.key} className="custom-thead-bg">{col.label}</th>
                ))}
              </tr>
            </thead>

            <tbody>
              {isLoading ? (
                <tr>
                  <td colSpan={ACTIVITY_COLUMNS.length + 1} className="text-center">
                    <Spinner animation="border" size="sm" />
                  </td>
                </tr>
              ) : activities.length === 0 ? (
                <tr>
                  <td colSpan={ACTIVITY_COLUMNS.length + 1} className="text-center">
                    {searchTerm.trim() ? "No activities found for your search." : "No activities found."}
                  </td>
                </tr>
              ) : (
                activities.map((activity, index) => (
                  <tr key={activity.id}>
                    {/* <td className="custom-tbody">{getSerialNumber(index)}</td> */}
                    {ACTIVITY_COLUMNS.map(col => (
                      <td key={col.key} className="custom-tbody">{renderTableCell(col.key, activity)}</td>
                    ))}
                  </tr>
                ))
              )}
            </tbody>
          </Table>
        </div>

				 	{activities?.length > 0 && (
						<PaginationComponent
							currentPage={paginationData.currentPage}
							totalPages={paginationData.totalPages}
							hasNextPage={paginationData.currentPage < paginationData.totalPages}
							hasPreviousPage={paginationData.currentPage > 1}
							nextPage={paginationData.currentPage + 1}
							previousPage={paginationData.currentPage - 1}
							onPaginate={handlePaginate}
						/>
					)}
      </div>
    </>
  );
};

export default ActivityLog;
